import { SalesforceConfigurationType } from './ai';
import {
  ApexClassProperties,
  ApexTriggerProperties,
  ApprovalProcessProperties,
  BaseFieldProperties,
  ConfigurationOrFieldProperties,
  CpqRecordProperties,
  FlowProperties,
  LayoutProperties,
  RecordTypeProperties,
  ValidationRuleProperties,
  WorkflowRuleProperties,
} from './documentation';

// Salesforce configurations
// TODO SWEEP-8247 or SWEEP-8083 - move this to a general file, e.g salesforce.ts
//  in shared-with-client (assuming FE are using them), as these are not parser-only consts
export const PATH_ASSISTANT = 'PathAssistant';
export const VALIDATION_RULE = 'ValidationRule';
export const APPROVAL_PROCESS = 'ApprovalProcess';
export const WORKFLOW_RULE = 'WorkflowRule';
export const FLOW = 'Flow';
export const APEX_CLASS = 'ApexClass';
export const FIELD = 'Field';
export const PROCESS_BUILDER_FLOW = 'ProcessBuilderFlow';
export const LAYOUT = 'Layout';
export const APEX_TRIGGER = 'ApexTrigger';
export const EMAIL_TEMPLATE = 'EmailTemplate';
export const REPORT = 'Report';
export const RECORD_TYPE = 'RecordType';

// TODO SWEEP-8083 - remove this and move to using the metadata type
export enum ConfigurationType {
  validationRules = 'validationRules',
  approvalProcesses = 'approvalProcesses',
  workflowRules = 'workflowRules',
  flows = 'flows',
  processBuilderFlows = 'processBuilderFlows',
  apexTriggers = 'apexTriggers',
  apexClasses = 'apexClasses',
  fields = 'fields',
  reports = 'reports',
  layouts = 'layouts',
  emailTemplates = 'emailTemplates',
  objects = 'objects', // search only
  sweepAutomation = 'automations',
  sweepAlert = 'alerts',
  sweepDedupe = 'dedup',
  sweepMatching = 'matching',
  sweepAssignment = 'assignments',
  sweepScheduledAssignment = 'scheduledAssignments',
  rollups = 'rollups',
  cpqData = 'cpqData',
  recordTypes = 'recordTypes',
  relatedObjects = 'relatedObjects',
  others = 'others',
  unclassifiedConfigurations = 'unclassifiedConfigurations', // search only
}

// TODO SWEEP-8083 - @karolinap  remove OpenAIConfigurationKeys and configurationKeyToType
export enum OpenAIConfigurationKeys {
  VALIDATION_RULES = 'validationRules',
  APPROVAL_PROCESSES = 'approvalProcesses',
  WORKFLOW_RULES = 'workflowRules',
  PROCESS_BUILDER_FLOWS = 'processBuilderFlows',
  FLOWS = 'flows',
  LAYOUTS = 'layouts',
  APEX_TRIGGERS = 'apexTriggers',
  APEX_CLASSES = 'apexClasses',
  CPQ_DATA = 'cpqData',
}

export const configurationKeyToType: {
  [key in Exclude<
    OpenAIConfigurationKeys,
    OpenAIConfigurationKeys.CPQ_DATA
  >]: SalesforceConfigurationType;
} = {
  [OpenAIConfigurationKeys.VALIDATION_RULES]: VALIDATION_RULE,
  [OpenAIConfigurationKeys.APPROVAL_PROCESSES]: APPROVAL_PROCESS,
  [OpenAIConfigurationKeys.WORKFLOW_RULES]: WORKFLOW_RULE,
  [OpenAIConfigurationKeys.PROCESS_BUILDER_FLOWS]: FLOW,
  [OpenAIConfigurationKeys.FLOWS]: FLOW,
  [OpenAIConfigurationKeys.LAYOUTS]: LAYOUT,
  [OpenAIConfigurationKeys.APEX_TRIGGERS]: APEX_TRIGGER,
  [OpenAIConfigurationKeys.APEX_CLASSES]: APEX_CLASS,
} as const;

export type Attributions = string[];

export interface FieldParserProperties extends BaseFieldProperties {
  picklistValues?: { name: string; label: string; attributions?: Attributions }[];
}

export interface RecordTypeParserProperties extends RecordTypeProperties {
  attributions?: Attributions;
}

export type RelatedElement = ConfigurationOrFieldProperties & {
  relatedObjects: string[];
};

export interface ParsedData {
  objectElements: ConfigurationOrFieldProperties[];
  relatedElements: RelatedElement[];
}

export interface ParsedObjectProperties {
  name: string;
  visible: boolean;
}

export type ParsedObjectData = ParsedObjectProperties & ParsedData;

// TODO SWEEP-8083 - remove all old types from here to bottom
export type GroupedParsedObjectData = ParsedObjectProperties & GroupedParsedData;

export type GroupedParsedData = {
  [ConfigurationType.recordTypes]: GroupedRecordTypeParserProperties[];
  [ConfigurationType.fields]: GroupedFieldParserProperties[];
  [ConfigurationType.validationRules]?: ValidationRuleProperties[];
  [ConfigurationType.approvalProcesses]?: ApprovalProcessProperties[];
  [ConfigurationType.workflowRules]?: WorkflowRuleProperties[];
  [ConfigurationType.processBuilderFlows]?: FlowProperties[];
  [ConfigurationType.flows]?: FlowProperties[];
  [ConfigurationType.layouts]?: LayoutProperties[];
  [ConfigurationType.apexTriggers]?: ApexTriggerProperties[];
  [ConfigurationType.apexClasses]?: ApexClassProperties[];
  [ConfigurationType.cpqData]?: CpqRecordProperties[];
  [ConfigurationType.relatedObjects]?: { [relatedObjectName: string]: RelatedObjectParsedData };
};

export type RelatedObjectParsedData = {
  [ConfigurationType.apexClasses]?: ApexClassProperties[];
  [ConfigurationType.apexTriggers]?: ApexTriggerProperties[];
  [ConfigurationType.fields]?: BaseFieldProperties[];
  [ConfigurationType.validationRules]?: ValidationRuleProperties[];
  [ConfigurationType.approvalProcesses]?: ApprovalProcessProperties[];
  [ConfigurationType.workflowRules]?: WorkflowRuleProperties[];
  [ConfigurationType.processBuilderFlows]?: FlowProperties[];
  [ConfigurationType.flows]?: FlowProperties[];
  [ConfigurationType.layouts]?: LayoutProperties[];
};

export type GroupedAttributions = {
  [ConfigurationType.layouts]?: string[];
  [ConfigurationType.validationRules]?: string[];
  [ConfigurationType.flows]?: string[];
  [ConfigurationType.processBuilderFlows]?: string[];
  [ConfigurationType.workflowRules]?: string[];
  [ConfigurationType.approvalProcesses]?: string[];
};

export interface GroupedFieldParserProperties extends BaseFieldProperties {
  picklistValues?: { name: string; label: string; attributions?: GroupedAttributions }[];
}

export interface GroupedRecordTypeParserProperties extends RecordTypeProperties {
  attributions?: GroupedAttributions;
}

// TODO SWEEP-8247 - change to NaclFieldType once we fix the issue with NaclFieldType in shared-with-client
//  should change in the input and in the array (equal NaclFieldType.MULTI_PICKLIST and NaclFieldType.PICKLIST)
export function isNaclPicklistField(fieldType: string): boolean {
  return ['Picklist', 'MultiselectPicklist'].includes(fieldType);
}
